import {
  X,
  ChefHat,
  MapPin,
  User,
  Clock,
  Package,
  CheckCircle,
  AlertCircle,
  ArrowRight,
  Calendar,
  Play,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/classnames';
import type {
  PrepList, PrepItem, PrepItemStatus 
} from '../../types';
import {
  PREP_LIST_STATUS_LABELS,
  PREP_LIST_STATUS_COLORS,
  MEAL_PERIOD_LABELS,
  RECIPE_TYPE_LABELS,
} from '../../types';

interface PrepListDetailModalProps {
  readonly prepList: PrepList | null;
  readonly isOpen: boolean;
  readonly onClose: () => void;
  readonly onActivate?: (prepListId: string) => void;
  readonly onComplete?: (prepListId: string) => void;
  readonly onStartItem?: (prepListId: string, itemId: string) => void;
  readonly onCompleteItem?: (prepListId: string, itemId: string) => void;
}

const WORKFLOW_STEPS = [
  {
    id: 'draft',
    label: 'Draft',
    description: 'Prep list created' 
  },
  {
    id: 'active',
    label: 'Active',
    description: 'In progress' 
  },
  {
    id: 'completed',
    label: 'Completed',
    description: 'All items done' 
  },
];

function getStepStatus(
  prepListStatus: PrepList['status'],
  stepId: string
): 'complete' | 'current' | 'upcoming' {
  const statusOrder = ['draft', 'active', 'completed'];
  const currentIndex = statusOrder.indexOf(prepListStatus);
  const stepIndex = statusOrder.indexOf(stepId);

  if (stepIndex < currentIndex) return 'complete';
  if (stepIndex === currentIndex) return 'current';
  return 'upcoming';
}

function WorkflowTimeline({ status }: { readonly status: PrepList['status'] }) {
  return (
    <div className="relative">
      <div className="flex items-center justify-between">
        {WORKFLOW_STEPS.map((step, index) => {
          const stepStatus = getStepStatus(status, step.id);
          const isLast = index === WORKFLOW_STEPS.length - 1;

          return (
            <div key={step.id} className="flex flex-1 items-center">
              <div className="flex flex-col items-center">
                <div
                  className={cn(
                    'flex h-10 w-10 items-center justify-center rounded-full border-2 transition-colors',
                    stepStatus === 'complete' && 'border-green-500 bg-green-500 text-white',
                    stepStatus === 'current' && 'border-primary bg-primary text-white',
                    stepStatus === 'upcoming' && 'border-gray-300 bg-white text-gray-400'
                  )}
                >
                  {stepStatus === 'complete' && <CheckCircle className="h-5 w-5" />}
                  {stepStatus === 'current' && <Clock className="h-5 w-5" />}
                  {stepStatus === 'upcoming' && <span className="text-sm font-medium">{index + 1}</span>}
                </div>
                <div className="mt-2 text-center">
                  <p
                    className={cn(
                      'text-sm font-medium',
                      stepStatus === 'upcoming' ? 'text-gray-400' : 'text-gray-900'
                    )}
                  >
                    {step.label}
                  </p>
                  <p className="text-xs text-gray-500">{step.description}</p>
                </div>
              </div>
              {isLast ? null : (
                <div className="mx-2 h-0.5 flex-1 self-start mt-5">
                  <div
                    className={cn(
                      'h-full transition-colors',
                      getStepStatus(status, WORKFLOW_STEPS[index + 1].id) === 'upcoming'
                        ? 'bg-gray-200'
                        : 'bg-green-500'
                    )}
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function getItemStatusBadge(status: PrepItemStatus) {
  switch (status) {
    case 'completed':
      return (
        <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2 py-1 text-xs font-medium text-green-800">
          <CheckCircle className="h-3 w-3" /> Complete
        </span>
      );
    case 'in-progress':
      return (
        <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-1 text-xs font-medium text-amber-800">
          <Clock className="h-3 w-3" /> In Progress
        </span>
      );
    default:
      return (
        <span className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-2 py-1 text-xs font-medium text-gray-600">
          <AlertCircle className="h-3 w-3" /> Pending
        </span>
      );
  }
}

interface PrepItemsTableProps {
  readonly items: PrepItem[];
  readonly prepListId: string;
  readonly prepListStatus: PrepList['status'];
  readonly onStartItem?: (prepListId: string, itemId: string) => void;
  readonly onCompleteItem?: (prepListId: string, itemId: string) => void;
}

function PrepItemsTable({
  items,
  prepListId,
  prepListStatus,
  onStartItem,
  onCompleteItem,
}: PrepItemsTableProps) {
  const canModifyItems = prepListStatus === 'active';

  return (
    <div className="overflow-hidden rounded-lg border">
      <table className="w-full">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
              Recipe
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
              Type
            </th>
            <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">
              Target
            </th>
            <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">
              Completed
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
              Assigned To
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
              Status
            </th>
            {canModifyItems && (
              <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">
                Actions
              </th>
            )}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200 bg-white">
          {items.map((item) => (
            <tr key={item.itemId} className="hover:bg-gray-50">
              <td className="px-4 py-3">
                <div className="font-medium text-gray-900">{item.recipeName}</div>
                {item.notes && <div className="text-xs text-gray-500">{item.notes}</div>}
              </td>
              <td className="px-4 py-3 text-sm text-gray-500">
                <span className="rounded bg-gray-100 px-2 py-0.5 text-xs">
                  {RECIPE_TYPE_LABELS[item.recipeType]}
                </span>
              </td>
              <td className="px-4 py-3 text-right text-sm text-gray-900">
                {item.targetQuantity} {item.unit}
              </td>
              <td className="px-4 py-3 text-right text-sm text-gray-900">
                {item.completedQuantity} {item.unit}
              </td>
              <td className="px-4 py-3 text-sm text-gray-500">
                {item.assignedToName ?? '—'}
              </td>
              <td className="px-4 py-3">{getItemStatusBadge(item.status)}</td>
              {canModifyItems && (
                <td className="px-4 py-3 text-right">
                  <div className="flex justify-end gap-2">
                    {item.status === 'pending' && onStartItem && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => onStartItem(prepListId, item.itemId)}
                      >
                        <Play className="mr-1 h-3 w-3" />
                        Start
                      </Button>
                    )}
                    {item.status === 'in-progress' && onCompleteItem && (
                      <Button
                        size="sm"
                        onClick={() => onCompleteItem(prepListId, item.itemId)}
                      >
                        <CheckCircle className="mr-1 h-3 w-3" />
                        Complete
                      </Button>
                    )}
                  </div>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function PrepListDetailModal({
  prepList,
  isOpen,
  onClose,
  onActivate,
  onComplete,
  onStartItem,
  onCompleteItem,
}: PrepListDetailModalProps) {
  if (!isOpen || !prepList) return null;

  const prepDate = new Date(prepList.date);
  const createdDate = new Date(prepList.createdAt);
  const progress =
    prepList.totalItems > 0
      ? Math.round((prepList.completedItems / prepList.totalItems) * 100)
      : 0;
  const canActivate = prepList.status === 'draft';
  const canComplete = prepList.status === 'active' && prepList.completedItems === prepList.totalItems;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="w-full max-w-4xl max-h-[90vh] overflow-y-auto rounded-lg bg-background shadow-xl">
        {/* Header */}
        <div className="sticky top-0 z-10 flex items-start justify-between border-b bg-background p-6">
          <div className="flex items-center gap-4">
            <div className="rounded-lg bg-primary/10 p-3">
              <ChefHat className="h-6 w-6 text-primary" />
            </div>
            <div>
              <div className="flex items-center gap-3">
                <h2 className="text-xl font-semibold">{prepList.venueName}</h2>
                <span
                  className={cn(
                    'rounded-full px-3 py-1 text-sm font-medium',
                    PREP_LIST_STATUS_COLORS[prepList.status]
                  )}
                >
                  {PREP_LIST_STATUS_LABELS[prepList.status]}
                </span>
              </div>
              <p className="flex items-center gap-2 text-sm text-muted-foreground">
                <MapPin className="h-4 w-4" />
                {MEAL_PERIOD_LABELS[prepList.mealPeriod]} Service
              </p>
            </div>
          </div>
          <button type="button" onClick={onClose} className="rounded-full p-2 hover:bg-muted">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Summary Cards */}
          <div className="grid grid-cols-4 gap-4">
            <div className="rounded-lg border bg-gray-50 p-4">
              <div className="flex items-center gap-2 text-sm text-gray-500">
                <Calendar className="h-4 w-4" />
                Prep Date
              </div>
              <p className="mt-1 font-medium">{prepDate.toLocaleDateString()}</p>
              <p className="text-xs text-gray-500">{MEAL_PERIOD_LABELS[prepList.mealPeriod]}</p>
            </div>
            <div className="rounded-lg border bg-gray-50 p-4">
              <div className="flex items-center gap-2 text-sm text-gray-500">
                <User className="h-4 w-4" />
                Created By
              </div>
              <p className="mt-1 font-medium">{prepList.createdByName}</p>
              <p className="text-xs text-gray-500">{createdDate.toLocaleDateString()}</p>
            </div>
            <div className="rounded-lg border bg-gray-50 p-4">
              <div className="flex items-center gap-2 text-sm text-gray-500">
                <Package className="h-4 w-4" />
                Total Items
              </div>
              <p className="mt-1 text-2xl font-bold">{prepList.totalItems}</p>
            </div>
            <div className="rounded-lg border bg-gray-50 p-4">
              <div className="flex items-center gap-2 text-sm text-gray-500">
                <CheckCircle className="h-4 w-4" />
                Progress
              </div>
              <p className="mt-1 text-2xl font-bold text-primary">{progress}%</p>
              <div className="mt-1 h-2 w-full rounded-full bg-gray-200">
                <div
                  className="h-2 rounded-full bg-primary transition-all"
                  style={{ width: `${progress}%` }}
                />
              </div>
            </div>
          </div>

          {/* Workflow Timeline */}
          <div className="rounded-lg border p-6">
            <h3 className="mb-4 flex items-center gap-2 font-semibold">
              <ArrowRight className="h-5 w-5" />
              Workflow Status
            </h3>
            <WorkflowTimeline status={prepList.status} />
          </div>

          {/* Item Status Summary */}
          <div className="grid grid-cols-3 gap-4">
            <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 text-center">
              <div className="flex items-center justify-center gap-2 text-gray-500">
                <AlertCircle className="h-4 w-4" />
                <span className="text-sm">Pending</span>
              </div>
              <p className="mt-1 text-2xl font-bold text-gray-600">
                {prepList.items.filter((i) => i.status === 'pending').length}
              </p>
            </div>
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-center">
              <div className="flex items-center justify-center gap-2 text-amber-600">
                <Clock className="h-4 w-4" />
                <span className="text-sm">In Progress</span>
              </div>
              <p className="mt-1 text-2xl font-bold text-amber-600">
                {prepList.items.filter((i) => i.status === 'in-progress').length}
              </p>
            </div>
            <div className="rounded-lg border border-green-200 bg-green-50 p-4 text-center">
              <div className="flex items-center justify-center gap-2 text-green-600">
                <CheckCircle className="h-4 w-4" />
                <span className="text-sm">Completed</span>
              </div>
              <p className="mt-1 text-2xl font-bold text-green-600">
                {prepList.items.filter((i) => i.status === 'completed').length}
              </p>
            </div>
          </div>

          {/* Prep Items List */}
          <div>
            <h3 className="mb-4 flex items-center gap-2 font-semibold">
              <Package className="h-5 w-5" />
              Prep Items ({prepList.items.length})
            </h3>
            <PrepItemsTable
              items={prepList.items}
              prepListId={prepList.prepListId}
              prepListStatus={prepList.status}
              onStartItem={onStartItem}
              onCompleteItem={onCompleteItem}
            />
          </div>
        </div>

        {/* Footer Actions */}
        <div className="sticky bottom-0 flex justify-end gap-3 border-t bg-background p-6">
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
          {canActivate && onActivate && (
            <Button onClick={() => onActivate(prepList.prepListId)}>
              <Play className="mr-2 h-4 w-4" />
              Activate Prep List
            </Button>
          )}
          {canComplete && onComplete && (
            <Button onClick={() => onComplete(prepList.prepListId)}>
              <CheckCircle className="mr-2 h-4 w-4" />
              Mark as Completed
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
